import { useState } from "react";
import { Modal } from "react-bootstrap";
import type { ConfirmStrategy } from "../types";

type ConfirmationRequiredModalProps = {
  show: boolean;
  reviewCount: number;
  confirmedCount: number;
  onCancel: () => void;
  onConfirm: (strategy: ConfirmStrategy) => Promise<void> | void;
};

const ConfirmationRequiredModal = ({ show, reviewCount, confirmedCount, onCancel, onConfirm }: ConfirmationRequiredModalProps) => {
  const [strategy, setStrategy] = useState<ConfirmStrategy | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    if (!strategy) return;
    setIsConfirming(true);
    try {
      await onConfirm(strategy);
    } finally {
      setIsConfirming(false);
    }
  };

  const options: { key: ConfirmStrategy; label: string }[] = [
    { key: "TOP_MATCH", label: `Choose the top matched companies for ${reviewCount} records.` },
    { key: "IGNORE_LOW", label: `Ignore these ${reviewCount} records and confirm for remaining ${confirmedCount} matched companies` },
  ];

  return (
    <Modal show={show} onHide={onCancel} centered animation={false} dialogClassName="tw-max-w-[734px] tw-w-full" contentClassName="!tw-rounded-lg !tw-border !tw-border-gray-200 !tw-p-0">
      <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-gray-200 tw-px-4 tw-py-4">
        <span className="tw-text-base tw-font-semibold tw-text-gray-900">Confirmation required</span>
        <button type="button" aria-label="Close" onClick={onCancel} className="tw-border-none tw-bg-transparent tw-p-0">
          <span className="material-symbols-outlined tw-text-xl tw-leading-none tw-text-gray-800">close</span>
        </button>
      </div>

      <div className="tw-px-4 tw-py-4">
        <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-gray-900">{reviewCount} companies are matched with less confidence score and needs user review.</p>
        <p className="tw-mb-0 tw-mt-1 tw-text-sm tw-font-medium tw-text-gray-900">You can choose following options:</p>

        <div className="tw-mt-5">
          {options.map((option, index) => (
            <div key={option.key}>
              {index > 0 && <div className="tw-my-3 tw-h-px tw-bg-gray-200" />}
              <button type="button" onClick={() => setStrategy(option.key)} className="tw-flex tw-w-full tw-items-center tw-gap-3 tw-border-none tw-bg-transparent tw-p-0 tw-text-left">
                <span className={`tw-flex tw-h-5 tw-w-5 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-border ${strategy === option.key ? "tw-border-primary-purple-600 tw-bg-primary-purple-50" : "tw-border-gray-300 tw-bg-white"}`}>{strategy === option.key && <span className="tw-h-2 tw-w-2 tw-rounded-full tw-bg-primary-purple-600" />}</span>
                <span className="tw-text-sm tw-font-medium tw-text-gray-700">{option.label}</span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="tw-flex tw-justify-end tw-border-t tw-border-gray-200 tw-px-4 tw-py-2">
        <button type="button" onClick={handleConfirm} disabled={!strategy || isConfirming} className="tw-h-8 tw-rounded-lg tw-border-none tw-bg-primary-purple-600 tw-px-3 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-primary-purple-800 disabled:tw-cursor-not-allowed disabled:tw-opacity-50">
          {isConfirming ? "Please wait…" : "Confirm"}
        </button>
      </div>
    </Modal>
  );
};

export default ConfirmationRequiredModal;
